/**
 * Skeleton Loading Components
 * Provides loading placeholders for data fetching states
 */

import React from 'react';
// @ts-ignore - JavaScript module without types
import { useTheme } from '../../../../context/ThemeContext';

/** One kit-style shimmer bar (New look loading language, see index.css .rn-sk). */
export function SkBar({ w = '100%', h = 10, r, className = '', style }: { w?: number | string; h?: number; r?: number; className?: string; style?: React.CSSProperties }) {
  return (
    <span
      className={`rn-sk ${className}`}
      aria-hidden="true"
      style={{ width: w, height: h, ...(r !== undefined ? { borderRadius: r } : null), ...style }}
    />
  );
}

const MONTH_COUNT = 12;
const cellW = (row: number, col: number) => 40 + (((row + 2) * (col + 5)) % 22);

function GridSkeletonRow({ kind, row }: { kind: 'section' | 'dept' | 'account' | 'net'; row: number }) {
  const ink = kind === 'net' ? 'rn-sk-ink' : '';
  const rowClass = {
    section: 'fv3-r-section bg-slate-50',
    dept: 'fv3-r-dept border-b border-slate-100',
    account: 'fv3-r-account border-b border-slate-50',
    net: 'fv3-r-net bg-slate-100 border-t-2 border-slate-300',
  }[kind];
  const pad = kind === 'account' || kind === 'section' ? 'py-2.5' : kind === 'net' ? 'py-3' : 'py-2';
  return (
    <tr className={rowClass}>
      {kind === 'account' ? (
        <td className="fv3-acct-cell sticky left-0 py-2 px-4 pl-14 z-20" style={{ background: 'var(--lodge-white)' }}>
          <div className="fv3-acct-label flex flex-col">
            <div className="fv3-acct-main fv3-sk-line20 flex items-center gap-1.5"><SkBar w={110 + ((row * 31) % 70)} h={11} /></div>
            <div className="fv3-acct-meta fv3-sk-line16 flex items-center gap-1.5 mt-0.5"><SkBar w={58 + ((row * 13) % 24)} h={14} r={4} /></div>
          </div>
        </td>
      ) : (
        <td className={`fv3-label-cell sticky left-0 ${pad} px-4 ${kind === 'dept' ? 'pl-8 bg-white' : kind === 'net' ? 'bg-slate-100' : 'bg-slate-50'} z-20`}>
          <div className="fv3-sk-line20"><SkBar w={kind === 'section' ? 84 : kind === 'net' ? 76 : 120 + ((row * 17) % 40)} h={11} className={ink} /></div>
        </td>
      )}
      {Array.from({ length: MONTH_COUNT }, (_, c) => (
        <td key={c} className={`text-right ${pad} px-3 tabular-nums`}>
          <div className="fv3-sk-line20"><SkBar w={cellW(row, c)} h={10} className={ink} style={{ marginLeft: 'auto' }} /></div>
        </td>
      ))}
      <td className={`text-right ${pad} px-4`}>
        <div className="fv3-sk-line20"><SkBar w={60} h={10} className={ink} style={{ marginLeft: 'auto' }} /></div>
      </td>
    </tr>
  );
}

/**
 * New-look grid skeleton: the live grid's own classes (fv3-grid, fv3-r-*,
 * fv3-col-*) so density, column widths and the sticky net footer land
 * exactly where the loaded grid puts them.
 */
function InsetGridSkeleton({ rows }: { rows: number }) {
  const perSection = Math.max(2, Math.ceil(rows / 2));
  let r = 0;
  const section = (key: string) => (
    <React.Fragment key={key}>
      <GridSkeletonRow kind="section" row={r++} />
      <GridSkeletonRow kind="dept" row={r++} />
      {Array.from({ length: perSection }, () => <GridSkeletonRow key={`a${r}`} kind="account" row={r++} />)}
    </React.Fragment>
  );
  return (
    <div className="fv3-grid-shell flex-1 min-h-0 flex flex-col overflow-hidden border border-slate-200 rounded-b-xl" aria-busy="true" aria-label="Loading forecast">
      <div className="fv3-grid-toolbar flex-shrink-0 bg-white border-b border-slate-100 px-3 py-1.5 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <SkBar w={58} h={12} />
          <SkBar w={148} h={24} r={8} />
          <SkBar w={24} h={24} r={6} />
          <SkBar w={24} h={24} r={6} />
          <SkBar w={24} h={24} r={6} />
        </div>
        <div className="flex items-center gap-2">
          <SkBar w={96} h={26} r={8} />
          <SkBar w={82} h={22} r={6} />
        </div>
      </div>
      <div className="flex-1 min-h-0 overflow-hidden">
        <table className="fv3-grid w-full text-sm border-collapse">
          <thead>
            <tr className="fv3-head-row border-b border-slate-100">
              <th className="fv3-col-account sticky top-0 left-0 bg-slate-50 text-left py-3 px-4 w-64 z-30"><SkBar w={70} h={10} /></th>
              {Array.from({ length: MONTH_COUNT }, (_, c) => (
                <th key={c} className="fv3-col-month sticky top-0 z-20 py-3 px-3 min-w-[100px] bg-slate-50">
                  <div className="flex justify-end"><SkBar w={30} h={10} /></div>
                </th>
              ))}
              <th className="fv3-col-total sticky top-0 bg-slate-50 py-3 px-4 min-w-[110px] z-20">
                <div className="flex justify-end"><SkBar w={34} h={10} /></div>
              </th>
            </tr>
          </thead>
          <tbody>
            {section('rev')}
            {section('exp')}
            <GridSkeletonRow kind="net" row={r++} />
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** Month calendar placeholder (daily entry): weekday band + 5×7 day cells. */
export function CalendarSkeleton() {
  return (
    <div className="fv3-sk-calendar" aria-busy="true" aria-label="Loading days">
      <div className="fv3-sk-cal-head">
        {Array.from({ length: 7 }, (_, i) => <SkBar key={i} w={26} h={9} />)}
      </div>
      <div className="fv3-sk-cal-grid">
        {Array.from({ length: 35 }, (_, i) => (
          <div className="fv3-sk-cal-cell" key={i}>
            <SkBar w={14} h={9} />
            <SkBar w="70%" h={14} r={4} style={{ marginTop: 'auto', marginLeft: 'auto' }} />
          </div>
        ))}
      </div>
    </div>
  );
}

/** Striped rows for tables/lists that load inside an existing frame. */
export function RowsSkeleton({ rows = 6, cols = 5, label = 'Loading' }: { rows?: number; cols?: number; label?: string }) {
  return (
    <div className="fv3-sk-rows" aria-busy="true" aria-label={label}>
      <div className="fv3-sk-rows-head" style={{ gridTemplateColumns: `minmax(0, 2fr) repeat(${cols - 1}, minmax(0, 1fr))` }}>
        {Array.from({ length: cols }, (_, c) => <SkBar key={c} w={c === 0 ? 72 : 48} h={9} style={c ? { marginLeft: 'auto' } : undefined} />)}
      </div>
      {Array.from({ length: rows }, (_, r) => (
        <div className="fv3-sk-rows-row" key={r} style={{ gridTemplateColumns: `minmax(0, 2fr) repeat(${cols - 1}, minmax(0, 1fr))` }}>
          {Array.from({ length: cols }, (_, c) => (
            <SkBar key={c} w={c === 0 ? 100 + ((r * 29) % 80) : cellW(r, c)} h={10} style={c ? { marginLeft: 'auto' } : undefined} />
          ))}
        </div>
      ))}
    </div>
  );
}

/** Detail-pane body placeholder: driver card, stat pair, list. */
export function PanelSkeleton({ items = 4 }: { items?: number }) {
  return (
    <div className="fv3-sk-panel" aria-busy="true" aria-label="Loading">
      <div className="fv3-sk-block">
        <SkBar w={90} h={9} />
        <SkBar w="70%" h={16} style={{ marginTop: 10 }} />
        <SkBar w="100%" h={32} r={8} style={{ marginTop: 12 }} />
      </div>
      <div className="fv3-sk-pair">
        <div className="fv3-sk-block"><SkBar w={60} h={9} /><SkBar w="60%" h={18} style={{ marginTop: 8 }} /></div>
        <div className="fv3-sk-block"><SkBar w={60} h={9} /><SkBar w="50%" h={18} style={{ marginTop: 8 }} /></div>
      </div>
      {Array.from({ length: items }, (_, i) => (
        <div className="fv3-sk-line" key={i}>
          <SkBar w={120 + ((i * 37) % 60)} h={10} />
          <SkBar w={48} h={10} />
        </div>
      ))}
    </div>
  );
}

// Base skeleton element with animation
export function Skeleton({ className = '' }: { className?: string }) {
  return (
    <div
      className={`sk-pulse animate-pulse rounded ${className}`}
      style={{ background: 'var(--lodge-onyx-100)' }}
      aria-hidden="true"
    />
  );
}

// Grid skeleton for P&L grid
export function GridSkeleton({ rows = 10 }: { rows?: number }) {
  const { insetActive } = useTheme() as { insetActive: boolean };
  if (insetActive) return <InsetGridSkeleton rows={rows} />;
  return (
    <div className="flex-1 overflow-hidden flex flex-col">
      {/* Toolbar skeleton */}
      <div className="px-4 py-2 flex items-center justify-between" style={{ borderBottom: '1px solid var(--lodge-onyx-50)' }}>
        <div className="flex items-center gap-2">
          <Skeleton className="h-6 w-16" />
          <Skeleton className="h-8 w-8" />
        </div>
        <div className="flex items-center gap-2">
          <Skeleton className="h-8 w-20" />
          <Skeleton className="h-8 w-20" />
          <Skeleton className="h-6 w-32" />
        </div>
      </div>

      {/* Grid skeleton */}
      <div className="flex-1 px-4 py-2">
        {/* Header row */}
        <div className="flex gap-2 py-3 mb-2" style={{ borderBottom: '1px solid var(--lodge-onyx-50)' }}>
          <Skeleton className="h-4 w-48" />
          <Skeleton className="h-4 w-16" />
          {Array.from({ length: 12 }).map((_, i) => (
            <Skeleton key={i} className="h-4 w-20 flex-shrink-0" />
          ))}
          <Skeleton className="h-4 w-24" />
        </div>

        {/* Section header */}
        <div className="flex gap-2 py-3 rounded mb-1" style={{ background: 'var(--lodge-onyx-50)' }}>
          <Skeleton className="h-5 w-24 ml-4" />
        </div>

        {/* Account rows */}
        {Array.from({ length: rows }).map((_, rowIndex) => (
          <div key={rowIndex} className="flex gap-2 py-3" style={{ borderBottom: '1px solid var(--lodge-onyx-50)' }}>
            <Skeleton className="h-4 w-48 ml-10" />
            <Skeleton className="h-5 w-16" />
            {Array.from({ length: 12 }).map((_, i) => (
              <Skeleton key={i} className="h-4 w-20 flex-shrink-0" />
            ))}
            <Skeleton className="h-4 w-24" />
          </div>
        ))}
      </div>
    </div>
  );
}

// Table skeleton for versions page
export function TableSkeleton({ rows = 5 }: { rows?: number }) {
  return (
    <div className="p-4">
      {/* Header */}
      <div className="flex gap-4 py-3 mb-2" style={{ borderBottom: '1px solid var(--lodge-onyx-100)' }}>
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-4 w-20" />
        <Skeleton className="h-4 w-16" />
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-4 w-32 ml-auto" />
      </div>

      {/* Rows */}
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex gap-4 py-4 items-center" style={{ borderBottom: '1px solid var(--lodge-onyx-50)' }}>
          <Skeleton className="h-4 w-40" />
          <Skeleton className="h-6 w-20 rounded-full" />
          <Skeleton className="h-4 w-12" />
          <Skeleton className="h-4 w-28" />
          <div className="ml-auto flex gap-2">
            <Skeleton className="h-8 w-8 rounded" />
            <Skeleton className="h-8 w-8 rounded" />
            <Skeleton className="h-8 w-8 rounded" />
          </div>
        </div>
      ))}
    </div>
  );
}

// Card skeleton for metrics
export function MetricCardSkeleton() {
  return (
    <div className="fv3-sk-card rounded-xl p-4" style={{ background: 'var(--lodge-white)', border: '1px solid var(--lodge-onyx-100)', boxShadow: 'var(--shadow-sm)' }}>
      <Skeleton className="h-4 w-24 mb-2" />
      <Skeleton className="h-8 w-20 mb-1" />
      <Skeleton className="h-3 w-32" />
    </div>
  );
}

// List skeleton for assumptions/health items
export function ListSkeleton({ rows = 6 }: { rows?: number }) {
  return (
    <div className="space-y-3 p-4">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="fv3-sk-card rounded-lg p-4 flex justify-between items-center" style={{ background: 'var(--lodge-white)', border: '1px solid var(--lodge-onyx-100)' }}>
          <div className="flex-1">
            <Skeleton className="h-4 w-40 mb-2" />
            <Skeleton className="h-3 w-24" />
          </div>
          <Skeleton className="h-6 w-16 rounded-full" />
        </div>
      ))}
    </div>
  );
}

export default Skeleton;
